import React, { FC, useState, ChangeEvent, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../../hooks/useAppSelector";
import { useAppDispatch } from "../../../../hooks/useAppDispatch";
import { useTranslation } from "react-i18next";
import {
  clearFilters,
  FiltersState,
  resetPreviousFid,
  resetSelectedCard,
  setFilter,
} from "../../../../services/reducers/filters";
import Dropdown from "../../../ui-kit/dropdown/dropdown";
import RangeInput from "../../../ui-kit/range-input/range-input";
import ToggleSwitch from "../../../ui-kit/toggle-switch/toggle-switch";
import Tooltip from "../../../ui-kit/tooltip/tooltip";
import SearchBar from "../../../ui-kit/search-bar/search-bar";
import Button from "../../../ui-kit/button/button";
import ClosedFilterCard from "../closed-filter-card/closed-filter-card";
import { useFilters } from "../../../../hooks/useFilters";
import { Filters } from "../../../ui-kit/filters/filters";
import styles from "./filter.module.scss";
import arrowIcon from "../../../../images/arrow-left-purple.svg";
import { setLayersVisible } from "../../../../services/reducers/map";
import { extentEstateEn } from "../../../../services/actions/map";
import { getFilterConfigs, RangeFilterConfig } from "../get-filter-configs";
import usePopStateScroll from "../../../../hooks/usePopStateScroll";
import Input from "../../../ui-kit/Input/input";

interface UniversalFilterProps {
  filterType: "InvestmentOpportunities" | "Plots" | "SpacesForRent";
  onBack: () => void;
}

const Filter: FC<UniversalFilterProps> = ({ filterType, onBack }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const filterKey =
    filterType === "InvestmentOpportunities"
      ? "investmentOpportunities"
      : filterType === "Plots"
        ? "plots"
        : "spacesForRent";

  const labelMap = {
    Plots: t("filter.landPlots"),
    SpacesForRent: t("filter.spacesForRent"),
    InvestmentOpportunities: t("filter.investmentOpportunities"),
  };

  const { data, filteredData, filteredLayerIds } = useAppSelector(
    (state) => state.filters[filterKey],
  );
  const filters = useAppSelector(
    (state) => state.filters[filterKey].filters,
  ) as FiltersState;
  const additionalData = useAppSelector(
    (state) => state.filters[filterKey].additionalData,
  );
  const { activeFilters, addFilter, removeFilter, clearAll } = useFilters();
  const [searchInput, setSearchInput] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const visibleLayers = useAppSelector((state) => state.map.visibleLayersId);
  const city = useAppSelector((state) => state.filters[filterKey].filters.city);
  const config = useMemo(
    () => getFilterConfigs(filterType, additionalData, i18n.language),
    [filterType, additionalData, i18n.language],
  );
  const showResults =
    new URLSearchParams(location.search).get("res") === "true";

  const handleFilterChange = (key: string, value: any) => {
    dispatch(setFilter({ filterType, filters: { [key]: value }, language: i18n.language }));
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    dispatch(setFilter({ filterType, filters: { value: value } }));
    updateShowResults(!!value.trim());
  };

  const handleClearSearch = () => {
    setSearchInput("");
    dispatch(setFilter({ filterType, filters: { value: "" } }));
    updateShowResults(false);
  };

  const handleSearch = () => {
    setHasSearched(true);
    updateShowResults(true);
    if (city) dispatch(extentEstateEn({ estateEn: city }));
  };

  useEffect(() => {
    if (!showResults) return;
    const newIds = filteredLayerIds.filter((id) => !visibleLayers.includes(id));
    if (newIds.length > 0) {
      dispatch(setLayersVisible([...visibleLayers, ...newIds]));
    }
  }, [showResults, filteredLayerIds]);

  const handleReset = () => {
    dispatch(clearFilters({ filterType }));
    dispatch(
      setFilter({
        filterType,
        filters: {
          value: "",
          minArea: "",
          maxArea: "",
          plotNumber: "",
          vacant: true,
          landUses: [],
          floors: [],
          officeNumber: "",
          city: null,
          maxRate: "",
          minRate: "",
        },
      }),
    );
    clearAll();
    setSearchInput("");
    setHasSearched(false);
    updateShowResults(false);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const parsedFilters: any = {};
    params.forEach((value, key) => {
      if (key === "fid" || key === "res") return;
      if (key === "landUses") {
        parsedFilters[key] = value.split(",").map((val) => Number(val));
      } else if (key === "vacant") {
        parsedFilters[key] = value === "true";
      } else if (key === "city") {
        parsedFilters[key] = Number(value);
      } else if (value.indexOf(",") !== -1) {
        parsedFilters[key] = value.split(",");
      } else {
        parsedFilters[key] = value;
      }
    });

    dispatch(setFilter({ filterType, filters: parsedFilters }));
    if (parsedFilters.value) {
      setSearchInput(parsedFilters.value);
    }
  }, []);

  const syncActiveFilters = () => {
    clearAll();
    if (filters.value)
      addFilter(`${t("filter-labels.search")}: ${filters.value}`);
    if (filters.minArea)
      addFilter(`${t("filter-labels.minArea")}: ${filters.minArea}`);
    if (filters.maxArea)
      addFilter(`${t("filter-labels.maxArea")}: ${filters.maxArea}`);
    if (filters.minRate)
      addFilter(`${t("filter-labels.minRate")}: ${filters.minRate}`);
    if (filters.maxRate)
      addFilter(`${t("filter-labels.maxRate")}: ${filters.maxRate}`);
    if (filters.city) {
      const cityOption = config
        .find((c) => c.key === "city")
        ?.options?.get(filters.city);
      if (cityOption) addFilter(`${t("filter-labels.city")}: ${cityOption}`);
    }
    if (filterType === "InvestmentOpportunities" || filterType === "Plots") {
      if (filters.plotNumber)
        addFilter(`${t("filter-labels.plotNumber")}: ${filters.plotNumber}`);
      if (
        filters.landUses &&
        Array.isArray(filters.landUses) &&
        filters.landUses.length > 0
      ) {
        const landUses = config.find((c) => c.key === "landUses")?.options;
        if (landUses)
          filters.landUses
            .filter((value: number) => landUses.has(value))
            .forEach((value: number) => addFilter(landUses.get(value)!));
      }
      if (filters.vacant) addFilter(t("filter-labels.vacantPlots"));
    } else if (filterType === "SpacesForRent") {
      if (filters.officeNumber)
        addFilter(
          `${t("filter-labels.officeNumber")}: ${filters.officeNumber}`,
        );
      if (
        filters.floors &&
        Array.isArray(filters.floors) &&
        filters.floors.length > 0
      ) {
        filters.floors.forEach((value: string) => addFilter(value));
      }
    }
  };

  useEffect(() => {
    syncActiveFilters();
  }, [filters, filterType, config]);

  const handleRemoveFilter = (id: number) => {
    const filter = activeFilters.find((f) => f.id === id);
    if (filter) {
      if (filter.name.startsWith(`${t("filter-labels.search")}:`)) {
        handleFilterChange("value", "");
      } else if (filter.name.startsWith(`${t("filter-labels.minArea")}:`)) {
        handleFilterChange("minArea", "");
      } else if (filter.name.startsWith(`${t("filter-labels.maxArea")}:`)) {
        handleFilterChange("maxArea", "");
      } else if (filter.name.startsWith(`${t("filter-labels.city")}:`)) {
        handleFilterChange("city", "");
      } else if (filter.name.startsWith(`${t("filter-labels.minRate")}:`)) {
        handleFilterChange("minRate", "");
      } else if (filter.name.startsWith(`${t("filter-labels.maxRate")}:`)) {
        handleFilterChange("maxRate", "");
      } else if (filter.name.startsWith(`${t("filter-labels.plotNumber")}:`)) {
        handleFilterChange("plotNumber", "");
      } else if (
        filter.name.startsWith(`${t("filter-labels.officeNumber")}:`)
      ) {
        handleFilterChange("officeNumber", "");
      } else if (filter.name === t("filter-labels.vacantPlots")) {
        handleFilterChange("vacant", false);
      } else if (filters.landUses && Array.isArray(filters.landUses)) {
        const landUse = Array.from(
          config.find((c) => c.key === "landUses")?.options?.entries() ?? [],
        )?.find(
          ([key, value]) =>
            filters.landUses?.includes(key) && filter.name === value,
        );
        if (landUse) {
          const newValues = filters.landUses.filter(
            (val: number) => val !== landUse[0],
          );
          handleFilterChange("landUses", newValues);
        }
      } else if (
        filters.floors &&
        Array.isArray(filters.floors) &&
        filters.floors.includes(filter.name)
      ) {
        const newFloors = filters.floors.filter(
          (val: string) => val !== filter.name,
        );
        handleFilterChange("floors", newFloors);
      }
      removeFilter(id);
    }
  };

  const handleClickBack = () => {
    if (!showResults) {
      onBack();
    } else {
      const params = new URLSearchParams(location.search);
      params.delete("fid");
      params.delete("res");
      navigate({ search: params.toString() });
      dispatch(resetSelectedCard());
      dispatch(resetPreviousFid());
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    Object.entries(filters).forEach(([key, value]) => {
      if (key === "fid") return;
      if (Array.isArray(value)) {
        value.length > 0
          ? params.set(key, value.join(","))
          : params.delete(key);
      } else if (key === "vacant") {
        params.set(key, value ? "true" : "false");
      } else if (value) {
        params.set(key, value.toString());
      } else {
        params.delete(key);
      }
    });
    const newSearch = params.toString();
    if (newSearch !== location.search.slice(1)) {
      navigate({ search: newSearch }, { replace: true });
    }
  }, [filters]);

  const updateShowResults = (isVisible: boolean) => {
    const params = new URLSearchParams(location.search);
    const current = params.get("res") === "true";
    if (current === isVisible) return;
    if (isVisible) {
      params.set("res", "true");
    } else {
      params.delete("res");
    }
    navigate({ search: params.toString() });
  };

  const isSearchBySearchBar = Boolean(filters.value && filters.value.trim());
  const onlyShowVacant =
    activeFilters.length === 1 &&
    activeFilters[0].name === t("filter-labels.vacantPlots");
  let shouldRenderActiveFilters = false;

  if (showResults) {
    if (!isSearchBySearchBar) {
      shouldRenderActiveFilters = activeFilters.length > 0;
    }
  } else {
    shouldRenderActiveFilters = activeFilters.length > 0 && !onlyShowVacant;
  }

  const showResetButton = activeFilters.length > 0 && !onlyShowVacant;

  usePopStateScroll({ param: "fid", prefix: "closed-filter-card-" });

  return (
    <div className={styles.filters}>
      <div onClick={handleClickBack} className={styles.header}>
        <div className={styles.title}>
          <img src={arrowIcon} alt="arrow button" />
          <span>{labelMap[filterType]}</span>
        </div>
        <p className={showResults ? styles.resultFound : styles.available}>
          {filteredData.length ?? data.length} {t("filter.found")}
        </p>
      </div>
      {(!showResults || isSearchBySearchBar) && (
        <SearchBar
          placeholder={t("filter.searchPlaceholder")}
          searchValue={searchInput}
          onSearchChange={handleSearchChange}
          onClearSearch={handleClearSearch}
        />
      )}
      {shouldRenderActiveFilters && (
        <div className={styles.activeFiltersContainer}>
          <Filters
            activeFilters={activeFilters}
            removeFilter={(id: number) => handleRemoveFilter(id)}
            closeFunc={() => updateShowResults(false)}
            clearAll={() => {
              clearAll();
              handleReset();
            }}
          />
        </div>
      )}
      {!showResults && (
        <div className={styles.container}>
          {config.map((filterConfig, index) => {
            switch (filterConfig.type) {
              case "text":
                return (
                  <div key={index} className={styles.field}>
                    <label className={styles.label}>
                      {t(filterConfig.label)}
                      {filterConfig.tooltip && (
                        <Tooltip text={t(filterConfig.tooltip)} />
                      )}
                    </label>
                    <Input
                      placeholder={
                        filterConfig.placeholder
                          ? t(filterConfig.placeholder)
                          : undefined
                      }
                      value={filters[filterConfig.key] || ""}
                      onChange={(newValue: string) =>
                        handleFilterChange(filterConfig.key, newValue)
                      }
                    />
                  </div>
                );
              case "dropdown":
                return (
                  <div key={index} className={styles.field}>
                    <label className={styles.label}>
                      {t(filterConfig.label)}
                      {filterConfig.tooltip && (
                        <Tooltip text={t(filterConfig.tooltip)} />
                      )}
                    </label>
                    <Dropdown
                      options={
                        filterConfig.options || new Map<number, string>()
                      }
                      value={filters[filterConfig.key] || ""}
                      placeholder={
                        filterConfig.placeholder
                          ? t(filterConfig.placeholder)
                          : undefined
                      }
                      onChange={(value: any) =>
                        handleFilterChange(filterConfig.key, value)
                      }
                      listType={filterConfig.listType}
                      onClear={() => handleFilterChange(filterConfig.key, "")}
                    />
                  </div>
                );
              case "range":
                const rangeConfig = filterConfig as RangeFilterConfig;
                return (
                  <div key={index} className={styles.field}>
                    <label className={styles.label}>
                      {t(rangeConfig.label)}
                      {rangeConfig.tooltip && (
                        <Tooltip text={t(rangeConfig.tooltip)} />
                      )}
                    </label>
                    <RangeInput
                      minValue={filters[rangeConfig.minKey] || ""}
                      maxValue={filters[rangeConfig.maxKey] || ""}
                      onMinChange={(value) =>
                        handleFilterChange(rangeConfig.minKey, value)
                      }
                      onMaxChange={(value) =>
                        handleFilterChange(rangeConfig.maxKey, value)
                      }
                    />
                  </div>
                );
              case "toggle":
                return (
                  <div key={index} className={styles.toggle}>
                    <ToggleSwitch
                      isChecked={!!filters[filterConfig.key]}
                      onChange={() =>
                        handleFilterChange(
                          filterConfig.key,
                          !filters[filterConfig.key],
                        )
                      }
                    />
                    <label className={styles.label}>
                      {t(filterConfig.label)}
                      {filterConfig.tooltip && (
                        <Tooltip text={t(filterConfig.tooltip)} />
                      )}
                    </label>
                  </div>
                );
              default:
                return null;
            }
          })}
        </div>
      )}
      {showResults && (
        <>
          {filteredData.length === 0 ? (
            <div className={styles.noResults}>
              <div className={styles.title}>
                <span>{t("filter.noMatches")}</span>
                <p>{t("filter.tryAgain")}</p>
              </div>
              <div className={styles.randomCardsContainer}>
                <span className={styles.title}>
                  {t("filter.mightBeInteresting")}
                </span>
                <div className={styles.randomCardsList}>
                  {data.length > 0 &&
                    [...data]
                      .sort(() => Math.random() - 0.5)
                      .slice(0, 10)
                      .map((item: any, index: number) => (
                        <ClosedFilterCard
                          key={index}
                          data={item}
                          type={filterType}
                        />
                      ))}
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.resultsContainer}>
              {filteredData.map((item: any, index: number) => (
                <ClosedFilterCard data={item} key={index} type={filterType} />
              ))}
            </div>
          )}
        </>
      )}
      {!showResults && (
        <div className={styles.buttonContainer}>
          <Button
            className={`${styles.btn} ${Object.values(filters).some((val) => val) ? "half" : "single"}`}
            type="primary"
            onClick={handleSearch}
          >
            {t("filter.search")}
          </Button>
          {showResetButton && (
            <Button className={styles.btn} type="outline" onClick={handleReset}>
              {t("filter.reset")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default Filter;
